import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

type CNCOperateAutodoorArgs = {
  argumentKind: 'CNCOperateAutodoor';
  deviceID?: string;
  actionID?: string;
};

class CNCOperateAutodoorRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.CNCOperateAutodoor', 'CNCOperateAutodoor');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    const a = args as unknown as CNCOperateAutodoorArgs;
    this.checkDevice(a, ctx);
    this.checkAction(a);
  }

  /**
   * Ensure a CNC machine device is selected and exists in equipment.
   *
   * @throws {ActionRequiredError}
   */
  private checkDevice(
    args: CNCOperateAutodoorArgs,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    if (!args.deviceID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Machine not selected',
        fieldId: 'deviceID',
      });
    }

    const equipment = ctx.equipment ?? [];
    const device = equipment.find((e) => e.id === args.deviceID);

    if (device == null || device.config?.kind !== 'CNCMachine') {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: 'Machine not available',
        fieldId: 'deviceID',
      });
    }
  }

  /**
   * Ensure an action is selected.
   *
   * @throws {ActionRequiredError}
   */
  private checkAction(args: CNCOperateAutodoorArgs): void {
    if (!args.actionID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Action is required',
        fieldId: 'actionID',
      });
    }
  }
}

export const CNCOperateAutodoorValidation: ValidationRule =
  new CNCOperateAutodoorRule().toValidationRule();
